import { IconChevronChevronLeft } from '@doltech/icons/IconChevronChevronLeft';
import { IconChevronChevronRight } from '@doltech/icons/IconChevronChevronRight';
import { ScrollType, smoothScroll } from '@doltech/utils/lib/smooth-scroll';
import cl from 'classnames';
import { noop } from 'lodash';
import * as React from 'react';
import styled from 'styled-components';

import { colorsV2 } from '../colors-v2';
import { StoryCard, StoryCardItem } from './StoryCard';
import { useScrollElement } from '@doltech/core/lib/hooks/useScrollElement';
import { toScreen } from '@doltech/core/lib/responsive/responsive.util';

const Main = styled.div`
  width: 100%;
  position: relative;
  .swiper-container {
    position: relative;
    display: grid;
    grid-auto-flow: column;
    gap: 8px;
    overflow: auto;
    -ms-overflow-style: none;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }

  .swiper-navigation {
    width: 40px;
    height: 40px;
    min-width: 40px;
    min-height: 40px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    line-height: 0;
    border: 1px solid ${colorsV2.white100};
    background: ${colorsV2.white100};
    color: ${colorsV2.black100};
    font-size: 20px;
    transition: all 0.3s ease;
    cursor: pointer;
    position: absolute;
    z-index: 2;
    &.next {
      top: calc(50% - 16px);
      right: 13px;
    }
    &.prev {
      top: calc(50% - 16px);
      left: 13px;
    }
    &.disabled {
      display: none;
    }
    &:hover {
      transform: scale(1.1);
    }
    &:active {
      transform: scale(0.9);
    }
    ${toScreen(1023)} {
      &.next,
      &.prev {
        display: none;
      }
    }
  }
`;

const StyledSwiper = styled.div``;

interface StoryListProps {
  data: StoryCardItem[];
  onCardClick?: (cardIndex: number, card: StoryCardItem) => void;
}

export const StoryList = (props: StoryListProps) => {
  const { data, onCardClick = noop } = props;
  const swiperRef = React.useRef<HTMLElement>();
  const { x: scrollLeft } = useScrollElement(swiperRef);
  return (
    <Main className="story-list">
      <StyledSwiper ref={swiperRef as any} className="swiper-container">
        {data.map((card, cardIndex) => {
          return (
            <StoryCard
              key={card.id || cardIndex}
              onClick={() => {
                onCardClick(cardIndex, card);
              }}
              variant="compact"
              {...card}
            />
          );
        })}
      </StyledSwiper>
      <div className="header-actions">
        <div
          aria-hidden
          onClick={() => {
            if (swiperRef.current) {
              smoothScroll(
                300,
                swiperRef.current,
                Math.max(scrollLeft - swiperRef.current.clientWidth, 0),
                ScrollType.HORIZONTAL
              );
            }
          }}
          className={cl('swiper-navigation prev', {
            disabled: scrollLeft === 0,
          })}
        >
          <IconChevronChevronLeft />
        </div>
        <div
          aria-hidden
          onClick={() => {
            if (swiperRef.current) {
              smoothScroll(
                300,
                swiperRef.current,
                Math.min(
                  scrollLeft + (swiperRef?.current?.clientWidth || 0),
                  swiperRef.current.scrollWidth
                ),
                ScrollType.HORIZONTAL
              );
            }
          }}
          className={cl('swiper-navigation next', {
            disabled:
              scrollLeft + (swiperRef?.current?.clientWidth || 0) >=
              (swiperRef?.current?.scrollWidth || 1),
          })}
        >
          <IconChevronChevronRight />
        </div>
      </div>
    </Main>
  );
};
